import { SurveyState, SurveyStateMapping } from '@context/UserContext/typing'
import localStore from '@service/local-store'

/**
 * Enum-like constants for survey state values.
 * Use these instead of raw string literals to avoid typos and enable refactoring.
 */
export const SURVEY_STATES = {
  NOT_STARTED: 'Not Started',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
} as const satisfies Record<string, SurveyState>

/**
 * Local storage survey state value (distinct from the user model's SurveyState).
 */
export const LOCAL_SURVEY_STATE = {
  STARTED: 'Started',
} as const

/**
 * Safely retrieves the first survey state value from a SurveyStateMapping.
 * Returns 'Not Started' when the mapping is empty, undefined, or has no entries.
 */
export const getFirstSurveyState = (
  surveyState: SurveyStateMapping | undefined
): SurveyState => {
  if (!surveyState) return SURVEY_STATES.NOT_STARTED
  const values = Object.values(surveyState)
  return values.length > 0 ? values[0] : SURVEY_STATES.NOT_STARTED
}

/**
 * Safely retrieves the first survey ID (key) from a SurveyStateMapping.
 * Returns an empty string when the mapping is empty or undefined.
 */
export const getFirstSurveyId = (
  surveyState: SurveyStateMapping | undefined
): string => {
  if (!surveyState) return ''
  const keys = Object.keys(surveyState)
  return keys.length > 0 ? keys[0] : ''
}

/**
 * Returns the state for a specific survey ID, falling back to 'Not Started'.
 */
export const getSurveyStateById = (
  surveyState: SurveyStateMapping | undefined,
  surveyId: string
): SurveyState => {
  if (!surveyState || !surveyId) return SURVEY_STATES.NOT_STARTED
  if (surveyState[surveyId]) return surveyState[surveyId]

  const normalizeSurveyId = (value: string): string =>
    value.replace(/[-_]/g, '').toLowerCase()

  const normalizedSurveyId = normalizeSurveyId(surveyId)
  const matchedEntry = Object.entries(surveyState).find(
    ([key]) => normalizeSurveyId(key) === normalizedSurveyId
  )

  return matchedEntry ? matchedEntry[1] : SURVEY_STATES.NOT_STARTED
}

/** Check helpers — each handles undefined/empty gracefully. */

export const isSurveyCompleted = (
  surveyState: SurveyStateMapping | undefined
): boolean => getFirstSurveyState(surveyState) === SURVEY_STATES.COMPLETED

export const isSurveyInProgress = (
  surveyState: SurveyStateMapping | undefined
): boolean => getFirstSurveyState(surveyState) === SURVEY_STATES.IN_PROGRESS

export const isSurveyNotStarted = (
  surveyState: SurveyStateMapping | undefined
): boolean => getFirstSurveyState(surveyState) === SURVEY_STATES.NOT_STARTED

export const isSurveyNotCompleted = (
  surveyState: SurveyStateMapping | undefined
): boolean => !isSurveyCompleted(surveyState)

/**
 * Returns true when the first survey state is either 'In Progress' or 'Not Started'
 * (i.e. the survey has not been completed yet and may or may not have been touched).
 */
export const isSurveyPending = (
  surveyState: SurveyStateMapping | undefined
): boolean => {
  const state = getFirstSurveyState(surveyState)
  return (
    state === SURVEY_STATES.IN_PROGRESS || state === SURVEY_STATES.NOT_STARTED
  )
}

export const SURVEY_SPECIALTIES = {
  PRIMARY_CARE: 'Primary care',
  OBGYN: 'OB-GYN',
  PRIMARY_CARE_SPECIALTY_TYPE: 's1',
  OBGYN_SPECIALTY_TYPE: 's2',
} as const

export const getSurveyTypeFromStorage = (): string | null =>
  localStore.get('surveyType') ?? null

export const surveyUsesGroupTitleLayout = (
  surveyType: string | null
): boolean => surveyType === SURVEY_SPECIALTIES.OBGYN

export const isFirstTimeSurvey = (
  surveyState: SurveyStateMapping | undefined
): boolean => {
  if (!surveyState) return true
  const values = Object.values(surveyState)
  const isNotStarted = values.every(
    (value) => value === SURVEY_STATES.NOT_STARTED
  )
  if (isNotStarted) return true
  else return false
}

export const isAnySurveyCompleted = (
  surveyState: SurveyStateMapping | undefined
): boolean => {
  if (!surveyState) return false
  const values = Object.values(surveyState)
  const isCompleted = values.some((value) => value === SURVEY_STATES.COMPLETED)
  if (isCompleted) return true
  else return false
}

export const areAllSurveysCompleted = (
  surveyState: SurveyStateMapping | undefined
): boolean => {
  if (!surveyState) return false
  const values = Object.values(surveyState)
  return (
    values.length > 0 &&
    values.every((value) => value === SURVEY_STATES.COMPLETED)
  )
}
